import { createElement, Fragment, type ReactNode } from "react";
import { Link } from "@mui/material";
import { FamilyStatus } from "protogen/advisors_service_pb";
import { MemberFamilyPlanFlags } from "utils/memberFamilyPlan";

export function secretsVaultIntro(flags: MemberFamilyPlanFlags): string {
  const accessClause = flags.isDigitalSubscription
    ? "Access is limited to your family unless explicitly shared."
    : "Access is limited to your family and your Advisor.";
  return (
    "An encrypted, private vault for storing sensitive information and " +
    `passwords. ${accessClause} Every access is recorded for security and ` +
    "auditability."
  );
}

export function taskDiscoveryAddTaskClause(
  flags: MemberFamilyPlanFlags,
): string {
  return flags.isDigitalSubscription
    ? "Add a task to get help from your assistant right away"
    : "Add a task for your Advisor to work on right away";
}

function fayeSupportLink(): ReactNode {
  return createElement(
    Link,
    { href: "mailto:support@findfaye.com" },
    "Faye support",
  );
}

export function settingsPlanSubscriptionFooter(
  flags: MemberFamilyPlanFlags,
): ReactNode {
  if (flags.isDigitalSubscription) {
    return createElement(
      Fragment,
      null,
      "For subscription changes, use the billing link above. For platform help, contact ",
      fayeSupportLink(),
      ".",
    );
  }
  return createElement(
    Fragment,
    null,
    "If you'd like to update your subscription, contact your Advisor directly. " +
      "For any platform questions or concerns, contact ",
    fayeSupportLink(),
    ".",
  );
}

export function calendarImportedCalendarsExplainer(
  flags: MemberFamilyPlanFlags,
): string {
  return flags.isDigitalSubscription
    ? "Import your calendars so Faye can see availability for scheduling and suggest helpful tasks based on what's coming up."
    : "Import your calendars to help your Advisor see availability for appointments and spot more opportunities to help.";
}

export function memberCalendarImportSyncTodoDescription(
  flags: MemberFamilyPlanFlags,
): string {
  return flags.isDigitalSubscription
    ? "Link your calendar to Faye so scheduling and task suggestions can use your availability."
    : "Link your calendar to Faye so your Advisor can see availability and anticipate upcoming needs.";
}

export function tasksPageTaskInspirationSubtitle(
  flags: MemberFamilyPlanFlags,
): string {
  return flags.isDigitalSubscription
    ? "Add these common tasks to your todo list"
    : "Get ahead by requesting these common tasks from your Advisor.";
}

export function calendarTasksConnectCalendarSecondLine(
  flags: MemberFamilyPlanFlags,
): string {
  return flags.isDigitalSubscription
    ? "Faye can also use it to suggest tasks and keep scheduling context in sync."
    : "Your Advisor will also be able to use it to schedule appointments, and proactively suggest upcoming tasks.";
}

export function memberOnboardingWelcomeSubtitle(
  flags: MemberFamilyPlanFlags,
): string {
  return flags.isDigitalSubscription
    ? "Complete these steps to get the most out of Faye."
    : "Complete these steps to get the most out of working with your Advisor.";
}

export type ReferralProperties = {
  title: string;
  subtitle?: string;
  description?: string;
  code: string;
  chipLabel?: string;
  rewards?: { label: string; value: string; caption: string }[];
  rewardsFootnote?: string;
  cursor?: string;
  cannedText?: string;
  finePrint?: ReactNode;
  url?: string;
};

export function referralProperties(
  flags: MemberFamilyPlanFlags,
  advisorProfilePath?: string,
): ReferralProperties {
  if (flags.isDigitalSubscription) {
    return {
      title: "Give a 50% discount on one month of Faye",
      description:
        "Invite a friend to Faye. When they sign up they'll get a 50% discount on their first month.",
      code: "50off",
      url: "https://joinfaye.com",
      chipLabel: "50% OFF",
      cannedText:
        "Faye's AI has been a game changer for my family. It sends me a nightly preview of tomorrow, reminds my husband about the school recital (without telling him I asked), and alerts me when airfare drops below my target price. It's like having an EA for your home life — but smarter! Use my link and we'll both get a free month!",
      finePrint: createElement(
        "span",
        null,
        "Your friend gets a 50% discount on the first month when they sign up with your link.",
      ),
    };
  }
  const canInvite =
    flags.familyStatus === FamilyStatus.ACTIVE ||
    flags.familyStatus === FamilyStatus.TEST ||
    flags.familyStatus === FamilyStatus.TEST_HIDDEN ||
    flags.familyStatus === FamilyStatus.DEMO ||
    flags.familyStatus === FamilyStatus.PREACTIVATION;
  if (canInvite) {
    return {
      title: "Give 20%, Get $50",
      subtitle: "A little thank-you for spreading the word.",
      code: "20off",
      rewards: [
        {
          label: "For your friends",
          value: "20% off",
          caption: "Their first month",
        },
        {
          label: "For you",
          value: "$50 credit",
          caption: "When they become a client",
        },
      ],
      rewardsFootnote:
        "Earn $50 for every new paying client you refer. No limits!",
      cannedText:
        "I've been working with a Faye Advisor and I love it!! Think of Faye as an EA " +
        "for your home life - they pair you with a professional local assistant who " +
        "handles all your personal-life admin: travel bookings, finding home service " +
        "pros, planning/booking family activities, and any other pesky tasks that are " +
        "stealing your evenings and weekends. Use my link for 20% off your first month!",
      finePrint: advisorProfilePath
        ? createElement(
            Fragment,
            null,
            "We match based on Advisor availability and fit! If your friends want to work with your current Advisor, they can sign up directly from your ",
            createElement(
              Link,
              {
                href: `https://findfaye.com${advisorProfilePath}`,
                target: "_blank",
                rel: "noopener noreferrer",
              },
              "Advisor\u2019s page",
            ),
            ".",
          )
        : undefined,
    };
  }
  return {
    title: "Refer three friends, get a month of Faye for free!",
    code: "p",
    cannedText:
      "I've been loving my Faye Advisor! They help take care of home life tasks like booking travel, managing services, and keeping the family organized.",
  };
}

export function resolveFeatureSuggestionDescription(
  featureId: string,
  defaultDescription: string,
  flags: MemberFamilyPlanFlags,
): string {
  if (!flags.isDigitalSubscription) {
    return defaultDescription;
  }
  switch (featureId) {
    case "delegation-guide":
      return "Learn how to effectively delegate tasks to your assistant.";
    case "household-info":
      return "Share household details for less back and forth.";
    case "work-with-me-guide":
      return "Quick form to tell your assistant how to best work with you.";
    case "family-secrets":
      return "How to use family secrets to share sensitive information securely.";
    default:
      return defaultDescription;
  }
}
